import React from 'react';
import { TemplatePlaceholdersBlockProps } from './types';
import { Box, Button, Paper, TextField, Tooltip, Typography } from '@mui/material';
import styles from './TemplatePlaceholders.module.scss';
import { useTranslation } from 'react-i18next';
import ButtonDropDown from '../../../../../common/button-drop-down/ButtonDropDown';
import MessageModal from '../../../../../common/message-modal/MessageModal';
import InfoIcon from '@mui/icons-material/Info';
import { useAppSelector } from '../../../../../common/hooks/useAppSelector';
import { selectTemplatePlaceholdersIndexes } from '../../../../../slices/template-placeholders-indexes/templatePlaceholdersIndexes';
import { selectTemplatePlaceholders } from '../../../../../slices/template-placeholders/templatePlaceholdersSlice';
import TemplatePlaceholderRow from './TemplatePlaceholderRow';
import { v4 as uuidv4 } from 'uuid';

export const TemplatePlaceholdersBlock = React.memo(
  ({
    createTemplateInitial,
    createTemplate,
    goBack,
    documentHTMLId,
    deletePlaceholders,
    templateCreationStep,
    setTemplateCreationStep,
    setDeletePlaceholdersMessageVisible,
    deletePlaceholdersMessageVisible,
    category,
    setCategory,
    documentTypeLabel,
    setDocumentTypeLabel,
    documentWorkflowLabel,
    setDocumentWorkflowLabel,
    customLabel,
    setCustomLabel,
    templateName,
    setTemplateName,
    typeLabels,
    workflowsArray,
    categoryArray,
  }: TemplatePlaceholdersBlockProps) => {
    const { t } = useTranslation();
    const chosenPlaceholdersIndexes = useAppSelector(selectTemplatePlaceholdersIndexes);
    const templateArray = useAppSelector(selectTemplatePlaceholders);
    return (
      <>
        <Box className={styles.container}>
          <Box
            className='inputs-editable'
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              width: '100%',
              paddingRight: '16px',
            }}
          >
            <Box sx={{ maxHeight: '85%', overflow: 'auto' }} style={{ flexGrow: '1' }}>
              {templateCreationStep === 1 && (
                <Box className={styles.container}>
                  <Box className={styles.sectionHeader}>
                    <Typography variant='h6' className={styles.sectionTitle}>
                      {t('headers.properties')}
                    </Typography>
                  </Box>
                  <Paper className={styles.propertiesContainer} elevation={0}>
                    <Box className={styles.propertyItem}>
                      <TextField
                        fullWidth
                        label={t('labels.templateName')}
                        variant='outlined'
                        size='medium'
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                      />
                    </Box>
                    <Box className={styles.inputs_container}>
                      <Box className={styles.propertyItem}>
                        <ButtonDropDown
                          id='templatePlaceholdersProfileDropdown'
                          label={t('labels.profile')}
                          options={categoryArray}
                          onChange={(event) => {
                            setCategory(event.target.value);
                            setDocumentWorkflowLabel('');
                          }}
                          value={category}
                        />
                      </Box>
                      <Box className={styles.propertyItem}>
                        <ButtonDropDown
                          id='templatePlaceholdersDocumentWorkflowDropdown'
                          label={t('labels.documentWorkflow')}
                          options={workflowsArray}
                          onChange={(event) => setDocumentWorkflowLabel(event.target.value)}
                          value={documentWorkflowLabel}
                        />
                      </Box>
                      <Box className={styles.propertyItem}>
                        <ButtonDropDown
                          id='templatePlaceholdersDocumentTypeDropdown'
                          label={t('labels.documentType')}
                          options={typeLabels}
                          onChange={(event) => setDocumentTypeLabel(event.target.value)}
                          value={documentTypeLabel}
                        />
                      </Box>
                      <Box className={styles.propertyItem}>
                        <TextField
                          fullWidth
                          label={t('labels.customLabel')}
                          variant='outlined'
                          size='medium'
                          autoComplete='off'
                          value={customLabel}
                          onChange={(event) => setCustomLabel(event.target.value)}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <Box component='div' sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Tooltip
                                    title={t('messages.customLabelMessage')}
                                    arrow
                                    placement='top'
                                  >
                                    <InfoIcon
                                      fontSize='small'
                                      color='primary'
                                      className={'info-icon'}
                                    />
                                  </Tooltip>
                                </Box>
                              ),
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              )}
              {templateCreationStep === 2 && (
                <table style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th colSpan={4} style={{ textAlign: 'right', paddingBottom: '16px' }}>
                        <Button
                          id='templatePlaceholdersDeleteChosenPlaceholdersBtn'
                          className='btn-default btn-action-basic'
                          onClick={() => setDeletePlaceholdersMessageVisible(true)}
                        >
                          {t('buttons.deleteChosenPlaceholders')}
                        </Button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...templateArray.slice()].map((el, index) => (
                      <TemplatePlaceholderRow
                        documentHTMLId={documentHTMLId}
                        index={index}
                        id={el.id}
                        data={el.data}
                        key={uuidv4()}
                      />
                    ))}
                  </tbody>
                </table>
              )}
              {/*<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
              <Button
                className='btn-default btn-action-basic'
                onClick={() => addRow()}
                style={{ marginLeft: '0.5rem' }}
                type='button'
                role='button'
                variant='contained'
                size='medium'
              >
                +
              </Button>
            </Box>*/}
            </Box>
            <Box
              style={{
                display: 'flex',
                marginTop: 'auto',
                paddingTop: '16px',
                paddingBottom: '16px',
                gap: '8px',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                id='templatePlaceholdersGoBackBtn'
                type='button'
                className='btn-default btn-action-basic btn-cancel'
                role='button'
                variant='contained'
                size='large'
                onClick={() => {
                  if (templateCreationStep === 1) {
                    goBack();
                  } else {
                    setTemplateCreationStep(1);
                  }
                }}
              >
                {t('buttons.goBack')}
              </Button>
              {templateCreationStep === 1 && (
                <Button
                  id='templatePlaceholdersNextBtn'
                  type='button'
                  className='btn-default btn-action-basic'
                  role='button'
                  variant='contained'
                  size='large'
                  onClick={() => {
                    setTemplateCreationStep(2);
                    createTemplateInitial();
                  }}
                >
                  {t('buttons.next')}
                </Button>
              )}
              {templateCreationStep === 2 && (
                <Button
                  id='templatePlaceholdersCreateTemplateBtn'
                  type='button'
                  className='btn-default btn-action-basic'
                  role='button'
                  variant='contained'
                  size='large'
                  onClick={createTemplate}
                >
                  {t('buttons.createTemplate')}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
        {deletePlaceholdersMessageVisible && (
          <MessageModal
            open={deletePlaceholdersMessageVisible}
            modalTitle={t('dialogTitles.deletePlaceholders')}
            modalMessage={
              chosenPlaceholdersIndexes && chosenPlaceholdersIndexes.length > 0
                ? t('messages.deletePlaceholdersDialog')
                : t('messages.choosePlaceholdersFirst')
            }
            actionButtonText={t('buttons.goBack')}
            actionButtonFn={() => {
              setDeletePlaceholdersMessageVisible(false);
            }}
            goBackButtonText={
              chosenPlaceholdersIndexes && chosenPlaceholdersIndexes.length > 0
                ? t('buttons.delete')
                : undefined
            }
            onClose={() => {
              if (chosenPlaceholdersIndexes && chosenPlaceholdersIndexes.length > 0) {
                deletePlaceholders?.();
                setDeletePlaceholdersMessageVisible(false);
              } else {
                setDeletePlaceholdersMessageVisible(false);
              }
            }}
          />
        )}
      </>
    );
  }
);

TemplatePlaceholdersBlock.displayName = 'TemplatePlaceholdersBlock';
